import React, { useState } from 'react';
import { createOrGetConversation, sendMessage } from '../../utils/messaging';
import { useAuth } from '../../contexts/AuthContext';

const SendMessageModal = ({ isOpen, onClose, recipient, currentUserData }) => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!currentUser || !recipient) {
      setError('Unable to send message. Please try again.');
      return;
    }

    setSending(true);
    setError('');

    try {
      // Create or get conversation
      const conversation = await createOrGetConversation(
        currentUser.uid,
        recipient.id,
        currentUserData,
        recipient
      );

      // Send the message
      await sendMessage(
        conversation.id,
        currentUser.uid,
        `${currentUserData.firstName} ${currentUserData.lastName}`.trim(),
        recipient.id,
        message,
        'text'
      );

      setSuccess(true);
      setMessage('');
      
      // Auto-close after success
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setMessage('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content send-message-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send Message</h3>
          <button 
            onClick={handleClose} 
            className="modal-close-btn"
            disabled={sending}
          >
            ×
          </button>
        </div>

        {recipient && (
          <div className="message-recipient">
            <div className="recipient-info">
              <div className="recipient-photo">
                {recipient.profilePicture ? (
                  <img src={recipient.profilePicture} alt={recipient.firstName} />
                ) : (
                  <div className="default-photo">
                    {recipient.firstName?.[0] || '?'}
                  </div>
                )}
              </div>
              <div className="recipient-details">
                <div className="recipient-name">
                  {recipient.firstName} {recipient.lastName}
                </div>
                <div className="recipient-type">
                  {recipient.userType === 'provider' ? 'Provider' : 'Learner'}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="message-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              Message sent successfully! Redirecting to conversations...
            </div>
          )}

          <div className="form-group">
            <label htmlFor="message">Your Message</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Hi ${recipient?.firstName || 'there'}, I'd like to...`}
              rows="4"
              maxLength="1000"
              disabled={sending || success}
              required
            />
            <div className="character-count">
              {message.length}/1000
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-btn"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="send-btn"
              disabled={sending || !message.trim() || success}
            >
              {sending ? 'Sending...' : success ? 'Sent!' : 'Send Message'}
            </button>
          </div>
        </form>

        <div className="message-tips">
          <h4>💡 Tips for your first message:</h4>
          <ul>
            <li>Introduce yourself and what you'd like to practice</li>
            <li>Mention your availability or preferred schedule</li>
            <li>Ask specific questions about their coaching style</li>
            <li>Be polite and professional</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SendMessageModal;